import { Monitor } from "../../../../types/assessment/monitor.ts";
import { AwsGrantPresets } from "./aws.ts";
import { GcpGrantPresets, gcpOnlyPublicAccessFixGenerator } from "./gcp.ts";

export const GrantPresets = {
  ...AwsGrantPresets,
  ...GcpGrantPresets,
  "Unused Grants": Monitor({
    show: "grant",
    // TODO: ENG-5452 - Add Entra once we have data
    scopes: ["k8s", "workspace"],
    // nodes that cannot reach a used or unknown permission
    searchTerm:
      'usage:type:"unused" ^usage:type:!"unused" ^grant=isProviderManaged:"true" identity=status:"active"',
    label: "Unused grant",
    priority: "HIGH",
    cta: "No privileges given by this grant have been used in the last 90 days. Consider removing this grant.",
    remediation: (action: string) =>
      `No privileges given by this grant have been used in the last 90 days. P0 has automatically ${action} to remove this grant.`,
    description:
      "Grants for which no privileges have been used in the last 90 days.",
    vcsRemediateSupported: true,
  }),
  "Privileged External Service-Account Access": Monitor({
    scopes: ["all"],
    show: "grant",
    searchTerm:
      'grant=principalType:"service-account" identity=external:true risk:"CRITICAL"',
    label: "Privileged external service-account access",
    priority: "HIGH",
    cta:
      "This service account has privileged access to your organization, but its authentication is controlled outside the " +
      "assessed environment. Review this access to ensure that it is required.",
    description:
      "Privileged grant to service accounts with externally managed authentication.",
  }),
  "Privileged Cross-Resource Service-Account Access": Monitor({
    scopes: ["all"],
    show: "grant",
    searchTerm:
      'grant={principalType:"service-account" cross:true} ^identity=external:true risk:"CRITICAL"',
    label: "Privileged cross-resource service-account access",
    priority: "MEDIUM",
    cta:
      "This service account has privileged access to a resource outside its parent resource (viz., it is managed in a " +
      "different account, project, or subscription). Review this access to ensure that it is required.",
    description:
      "Privileged grant to service accounts on a different parent resource.",
  }),
  "Lateral Movement": Monitor({
    scopes: ["all"],
    show: "grant",
    searchTerm: 'entitlement=isProviderManaged:!"true"->lateral:->identity:',
    label: "Lateral movement",
    priority: "MEDIUM",
    cta:
      "This grant allows its principal to perform actions as one or more identities. " +
      "Review this access to ensure that it is required.",
    description:
      "Grants that allow principals to perform actions as another identity.",
  }),
  // This preset doesn't apply, as neither k8s nor workspace have usage tracked.
  // Re-enable as individual presets when we are tracking usage for those scopes.
  // Code is left for future reference only.
  // "Excess Privileges": Monitor({
  //   scopes: ["k8s", "workspace"],
  //   show: "grant",
  //   // nodes that can reach an unused permission AND can reach a used or unknown permission
  //   searchTerm: (
  //     'usage:type:"unused" usage:type:!"unused" ^grant=isProviderManaged:"true"'
  //   ),
  //   label: "Excess privileges",
  //   priority: "MEDIUM",
  //   cta: "Some of this grant's privileges have not been used in the last 90 days. Consider replacing this grant with a least-privileged role.",
  //   description:
  //     "Grants with some used and some unused privileges over the last 90 days.",
  //   fix: partialGrantFixGenerator,
  // }),
  "Privileged Access": Monitor({
    scopes: ["k8s", "workspace", "custom"],
    show: "grant",
    searchTerm: 'risk:"CRITICAL" ^grant=isProviderManaged:"true"',
    label: "Privileged access",
    priority: "MEDIUM",
    cta: "This grant allows access with critical risks. Consider replacing this grant with a low-sensitivity grant, and using just-in-time access for ephemeral access.",
    description: "Grants with any privileges that allow sensitive access.",
  }),
  // This preset doesn't apply, as neither k8s nor workspace have usage tracked.
  // Re-enable as individual presets when we are tracking usage for those scopes.
  // Code is left for future reference only.
  // "Unused Privileged Access": Monitor({
  //   scopes: ["k8s", "workspace"],
  //   show: "grant",
  //   searchTerm: (
  //     'usage:type:"unused"->risk:"CRITICAL" ^grant=isProviderManaged:"true"'
  //   ),
  //   label: "Unused privileged access",
  //   priority: "CRITICAL",
  //   cta: "This grant conveys privileges that allow critical risks, and these privileges have not been used in the last 90 days. Consider replacing this grant with a role that does not include these privileges.",
  //   description: "Grants with unused privileges that allow sensitive access.",
  // }),
  "Public Access": Monitor({
    scopes: ["aws", "gcloud", "k8s", "workspace", "custom"],
    show: "grant",
    searchTerm: 'resource: grant=principalType:"public"',
    label: "Public access",
    priority: "CRITICAL",
    cta: "This grant allows everyone on the internet access. Validate that this is intentional, and remove this grant if it is not.",
    description: "Grants that allow resource access to anyone on the Internet.",
    fix: gcpOnlyPublicAccessFixGenerator("enabled"),
    revert: gcpOnlyPublicAccessFixGenerator("disabled"),
  }),
};
